import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { HipaaLogo } from "~/icons";
import { officePreferenceSchema } from "~/schemas/SignUp/Location";
import { RadioCardGroup } from "~/shared/components/ui/RadioCardGroup";
import { useAutoAdvanceOnSelect } from "~/shared/hooks/useAutoAdvanceOnSelect";
import { useSubStepTracking } from "~/shared/hooks/useFunnelTracking";
import { NEW_YORK_OFFICES } from "~/shared/utils/constants";
import { SignUpFormProps } from "~/shared/utils/types";
import { useSignUpStore } from "~/stores";
import { ActionButtons } from "../../ActionButtons";
import { ErrorMessage } from "../../ErrorMessage";
import { FormContentContainer } from "../../FormContentContainer";

export const OfficePreferenceForm = ({
  onPrev,
  onNext,
  stepId = "office_prference",
}: SignUpFormProps & { stepId?: string }) => {
  const { patient, setPatientField } = useSignUpStore();
  const { preferredOfficeVisit } = patient;

  const defaultValue = preferredOfficeVisit;
  const [selectedValue, setSelectedValue] = useState<string>(
    defaultValue ?? "",
  );
  const autoAdvanceOnSelect = useAutoAdvanceOnSelect(Boolean(defaultValue));

  const { trackComplete, trackBack, trackErrors } = useSubStepTracking({
    parentStep: "location",
    subStep: "office",
  });

  const officeOptions = NEW_YORK_OFFICES.map(({ label, value }) => ({
    value,
    label,
  }));

  const {
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<z.infer<typeof officePreferenceSchema>>({
    mode: "onBlur",
    resolver: zodResolver(officePreferenceSchema),
    defaultValues: {
      office: defaultValue ?? "",
    },
  });

  useEffect(() => {
    setValue("office", defaultValue ?? "");
    setSelectedValue(defaultValue ?? "");
  }, [defaultValue, setValue]);

  const handleOptionChange = (value: string) => {
    setSelectedValue(value);
    setValue("office", value);
    // On the frontier (first encounter), a selection advances immediately;
    // on a revisited step the user changes freely and clicks Next.
    if (autoAdvanceOnSelect) {
      void handleSubmit(onSubmit, onError)();
    }
  };

  const onSubmit: SubmitHandler<z.infer<typeof officePreferenceSchema>> = (
    data,
  ) => {
    setPatientField("preferredOfficeVisit", data.office);
    trackComplete({ office_preference: data.office });
    onNext();
  };

  const onError = (errors: FieldErrors) => {
    trackErrors(errors);
  };

  const handlePrev = () => {
    setPatientField("preferredOfficeVisit", undefined);
    trackBack();
    onPrev();
  };

  const canProceed = selectedValue !== "";

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="rounded-lg shadow-xl"
    >
      <FormContentContainer stepId={stepId}>
        <HipaaLogo className="size-16 self-center sm:size-20" />
        <h1 className="font-display text-xl sm:text-2xl">
          Which office do you prefer to be seen out of?
        </h1>

        <RadioCardGroup
          options={officeOptions}
          value={selectedValue}
          onChange={handleOptionChange}
        />

        {errors.office && (
          <ErrorMessage message={errors.office.message ?? ""} />
        )}
      </FormContentContainer>
      <ActionButtons
        onPrev={handlePrev}
        isNextDisabled={!canProceed}
        helperText="Select an office to continue"
      />
    </form>
  );
};
